<template lang="html">
  <!--
    Desktop + mobile shells stay in the DOM; `lg:` picks which trigger is
    visible. Avoids VueUse useMediaQuery’s mount-delayed `false` painting
    the desktop field on phones before the mobile lookalike. Overlay open
    state is still JS-gated so a hidden shell cannot teleport UI into view.
  -->
  <!-- Desktop (lg+): real input + UPopover modal -->
  <div
    ref="rootRef"
    class="app-header-search pointer-events-auto relative z-60 hidden min-w-0 flex-1 lg:flex"
  >
    <!-- Holds layout while the field is lifted above the backdrop. -->
    <div
      v-if="isLifted"
      ref="spacerRef"
      class="app-header-search__spacer shrink-0"
      :style="spacerStyle"
      aria-hidden="true"
    />

    <!--
      Teleport when lifted: header uses backdrop-blur/sticky z-50, so a fixed
      child cannot paint above a body-level backdrop without leaving that context.
    -->
    <Teleport
      to="body"
      :disabled="!isLifted"
    >
      <div
        ref="liftRef"
        class="app-header-search__lift pointer-events-auto"
        :class="isLifted
          ? 'fixed z-60'
          : 'flex min-w-0 flex-1'"
        :style="liftStyle"
      >
        <UPopover
          v-model:open="desktopOpen"
          modal
          class="w-full min-w-0 flex-1"
          :content="{ align: 'start', side: 'bottom', sideOffset: 8, collisionPadding: 8 }"
          :ui="{ content: 'z-[70] p-0 overflow-hidden rounded-xl shadow-lg ring ring-default' }"
        >
          <div
            class="app-header-search__field group/search flex w-full min-w-0 flex-1 items-center rounded-lg border border-default bg-elevated transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30 hover:border-primary motion-reduce:transition-none"
            @focusin="openDesktopSearch"
            @click.capture="onDesktopFieldClickCapture"
          >
            <!--
              Leading lupa matches the mobile trigger so a late breakpoint
              resolve cannot flip the icon from trailing → leading (CLS).
            -->
            <div class="flex shrink-0 items-center ps-1.5">
              <UButton
                color="neutral"
                variant="ghost"
                size="sm"
                icon="i-lucide-search"
                square
                class="app-header-search__submit text-tertiary-500 transition-colors hover:bg-primary/10 hover:text-primary motion-reduce:transition-none"
                aria-label="Найти"
                @pointerdown.prevent.stop
                @click.prevent.stop="goToSearch"
              />
            </div>

            <input
              ref="inputRef"
              v-model="queryModel"
              type="text"
              name="q"
              autocomplete="off"
              enterkeyhint="search"
              placeholder="Поиск по сайту"
              class="app-header-search__input min-w-0 flex-1 border-0 bg-transparent py-2.5 ps-1 pe-4 text-sm text-highlighted outline-none placeholder:text-muted"
              aria-label="Поиск по сайту"
              aria-haspopup="dialog"
              :aria-expanded="desktopOpen"
              @keydown.enter.prevent="goToSearch"
            >

            <div
              v-if="queryModel"
              class="flex shrink-0 items-center pe-1.5"
            >
              <UButton
                color="neutral"
                variant="ghost"
                size="sm"
                icon="i-lucide-x"
                square
                class="text-muted hover:text-highlighted"
                aria-label="Очистить"
                @pointerdown.prevent.stop
                @click.prevent.stop="clearQuery"
              />
            </div>
          </div>

          <template #content>
            <div
              class="search-popover-panel flex max-h-[min(36rem,75dvh)] min-h-[min(28rem,70dvh)] flex-col overflow-hidden p-4 sm:p-5"
              :style="panelWidthStyle"
            >
              <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
                <SearchSuggests
                  v-if="debouncedQuery"
                  class="min-h-full flex-1"
                  :query="debouncedQuery"
                  @select="closeForNavigation"
                />
                <SearchEmptyPanel
                  v-else
                  class="min-h-full flex-1"
                  :history-items="historyItems"
                  @select="closeForNavigation"
                  @select-history="onSelectHistory"
                  @clear-history="clearHistory"
                />
              </div>
            </div>
          </template>
        </UPopover>
      </div>
    </Teleport>

    <!-- Full-viewport dim; lifted field + popover sit above (z-60 / z-70). -->
    <Teleport to="body">
      <div
        v-if="desktopOpen"
        class="app-header-search__backdrop pointer-events-auto fixed inset-0 z-55 bg-black/40"
        aria-hidden="true"
        @click="desktopOpen = false"
      />
    </Teleport>
  </div>

  <!-- Mobile (<lg): lookalike opens fullscreen modal -->
  <button
    type="button"
    class="app-header-search flex min-w-0 flex-1 cursor-pointer items-center gap-2 rounded-lg border border-default bg-elevated px-3 py-2 text-left text-sm text-muted transition-colors hover:border-primary hover:text-highlighted focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none motion-reduce:transition-none lg:hidden"
    aria-label="Поиск по сайту"
    aria-haspopup="dialog"
    :aria-expanded="mobileOpen"
    @click="emit('update:open', true)"
  >
    <UIcon
      name="i-lucide-search"
      class="size-4 shrink-0 text-tertiary-500"
    />
    <span class="min-w-0 flex-1 truncate">
      Поиск по сайту
    </span>
  </button>

  <UModal
    v-model:open="mobileOpen"
    fullscreen
    dismissible
    :ui="{ content: 'divide-none' }"
  >
    <template #content>
      <div class="search-modal relative flex h-full flex-col gap-4 overflow-hidden p-4">
        <h2 class="text-xl font-semibold">
          Поиск
        </h2>

        <div class="search-modal__input flex shrink-0 items-center gap-2">
          <div
            class="group/search flex min-w-0 flex-1 items-center rounded-lg border border-default bg-elevated transition-colors focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/30"
          >
            <div class="flex shrink-0 items-center ps-1.5">
              <UButton
                color="neutral"
                variant="ghost"
                size="sm"
                icon="i-lucide-search"
                square
                class="text-tertiary-500 hover:bg-primary/10 hover:text-primary"
                aria-label="Найти"
                @click.prevent.stop="goToSearch"
              />
            </div>
            <input
              ref="mobileInputRef"
              v-model="queryModel"
              type="text"
              name="q"
              autocomplete="off"
              enterkeyhint="search"
              placeholder="Поиск по сайту"
              class="app-header-search__input min-w-0 flex-1 border-0 bg-transparent py-2.5 ps-1 pe-4 text-sm text-highlighted outline-none placeholder:text-muted"
              aria-label="Поиск по сайту"
              @keydown.enter.prevent="goToSearch"
            >
            <div
              v-if="queryModel"
              class="flex shrink-0 items-center pe-1.5"
            >
              <UButton
                color="neutral"
                variant="ghost"
                size="sm"
                icon="i-lucide-x"
                square
                aria-label="Очистить"
                @click.prevent.stop="clearQuery"
              />
            </div>
          </div>
        </div>

        <UButton
          icon="i-lucide-x"
          color="neutral"
          variant="ghost"
          class="absolute top-2 right-2"
          aria-label="Закрыть"
          @click="mobileOpen = false"
        />

        <div class="flex min-h-0 flex-1 flex-col overflow-y-auto">
          <SearchSuggests
            v-if="debouncedQuery"
            class="min-h-full flex-1"
            :query="debouncedQuery"
            @select="closeForNavigation"
          />
          <SearchEmptyPanel
            v-else
            class="min-h-full flex-1"
            :history-items="historyItems"
            @select="closeForNavigation"
            @select-history="onSelectHistory"
            @clear-history="clearHistory"
          />
        </div>
      </div>
    </template>
  </UModal>
</template>

<script setup lang="ts">
import { useResizeObserver } from '@vueuse/core'
import { debounce } from 'lodash-es'

import SearchEmptyPanel from './SearchEmptyPanel.vue'
import SearchSuggests from './SearchSuggests.vue'

import { useDevice } from '#Common/composables/useDevice'
import { useCatalogMenu } from '#Products/composables/useCatalogMenu'
import { useSearchHistory } from '#Products/composables/useSearchHistory'

interface FieldMetrics {
  top: number
  left: number
  width: number
  height: number
}

const props = defineProps<{
  open: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  'close-without-restore': []
}>()

const { routing } = useProducts()
const {
  items: historyItems,
  add: addHistory,
  clear: clearHistory,
} = useSearchHistory()
const { close: closeCatalog } = useCatalogMenu()

/** Match `lg:` shells (CSS) — not `useDevice().isMobile` (`< md`). */
const { isBelowLg } = useDevice()

const rootRef = ref<HTMLElement | null>(null)
const liftRef = ref<HTMLElement | null>(null)
const spacerRef = ref<HTMLElement | null>(null)
const inputRef = ref<HTMLInputElement | null>(null)
const mobileInputRef = ref<HTMLInputElement | null>(null)
const triggerWidth = ref(0)
const isLifted = ref(false)
const fieldMetrics = ref<FieldMetrics | null>(null)

function readMetrics(el: HTMLElement): FieldMetrics {
  const rect = el.getBoundingClientRect()
  return {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  }
}

function captureFieldMetrics() {
  const el = liftRef.value
  if (!el) return false
  fieldMetrics.value = readMetrics(el)
  triggerWidth.value = Math.round(fieldMetrics.value.width)
  return true
}

function syncLiftToSpacer() {
  const spacer = spacerRef.value
  if (!spacer || !fieldMetrics.value) return
  const rect = spacer.getBoundingClientRect()
  fieldMetrics.value = {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: fieldMetrics.value.height,
  }
  triggerWidth.value = Math.round(rect.width)
}

const spacerStyle = computed(() => {
  const m = fieldMetrics.value
  if (!m) return undefined
  return {
    width: `${m.width}px`,
    height: `${m.height}px`,
  }
})

const liftStyle = computed(() => {
  if (!isLifted.value || !fieldMetrics.value) return undefined
  const m = fieldMetrics.value
  return {
    top: `${m.top}px`,
    left: `${m.left}px`,
    width: `${m.width}px`,
    height: `${m.height}px`,
  }
})

useResizeObserver(rootRef, (entries) => {
  const entry = entries[0]
  if (!entry) return

  if (isLifted.value) {
    syncLiftToSpacer()
    return
  }

  triggerWidth.value = Math.round(entry.contentRect.width)
})

const panelWidthStyle = computed(() => {
  if (!triggerWidth.value) {
    return { width: '100%', maxWidth: 'calc(100vw - 2rem)' }
  }
  return {
    width: `${triggerWidth.value}px`,
    maxWidth: 'calc(100vw - 2rem)',
  }
})

const query = ref('')
const debouncedQuery = ref('')

const debouncedSetQuery = debounce((value: string) => {
  debouncedQuery.value = value.trim()
}, 300)

const queryModel = computed({
  get: () => query.value,
  set: (value: string) => {
    query.value = value
    debouncedSetQuery(value)
  },
})

/** Only the active breakpoint may open its overlay (both shells stay mounted). */
const desktopOpen = computed({
  get: () => props.open && !isBelowLg.value,
  set: (value: boolean) => emit('update:open', value),
})

const mobileOpen = computed({
  get: () => props.open && isBelowLg.value,
  set: (value: boolean) => emit('update:open', value),
})

/**
 * Outside dismiss / Esc → useSearchModal restores focus to the input on nextTick.
 * Teleport unlift can also blur+refocus. Without this guard, @focusin immediately
 * reopens the popover (close → restore focus → focusin → open).
 */
const suppressOpenFromFocus = ref(false)

function openDesktopSearch() {
  if (suppressOpenFromFocus.value) return
  emit('update:open', true)
}

function onDesktopFieldClickCapture(event: MouseEvent) {
  const target = event.target
  if (!(target instanceof Element)) return

  const button = target.closest('button')
  if (button) {
    // Action buttons live inside Popover Trigger — block its toggle and run
    // the action here (stopImmediatePropagation in capture skips @click).
    const label = button.getAttribute('aria-label')
    if (label === 'Очистить') {
      event.stopImmediatePropagation()
      event.preventDefault()
      clearQuery()
      return
    }
    if (label === 'Найти') {
      event.stopImmediatePropagation()
      event.preventDefault()
      goToSearch()
      return
    }
    return
  }

  if (suppressOpenFromFocus.value) {
    event.stopImmediatePropagation()
    return
  }

  event.stopImmediatePropagation()
  openDesktopSearch()
}

function closeForNavigation() {
  emit('close-without-restore')
}

function clearQuery() {
  query.value = ''
  debouncedQuery.value = ''
  debouncedSetQuery.cancel()
  // Keep overlay open; refocus so unmounting the clear control cannot dismiss.
  emit('update:open', true)
  nextTick(() => {
    inputRef.value?.focus()
    mobileInputRef.value?.focus()
  })
}

function onSelectHistory(entry: string) {
  query.value = entry
  debouncedQuery.value = entry
  debouncedSetQuery.cancel()
  nextTick(() => {
    inputRef.value?.focus()
    mobileInputRef.value?.focus()
  })
}

function goToSearch() {
  const trimmed = query.value.trim()
  if (!trimmed) return

  addHistory(trimmed)
  closeForNavigation()
  navigateTo(routing.getSearch(trimmed).route)
}

function liftField() {
  // Measure while still in-flow, then take out of flow in the same tick.
  if (!captureFieldMetrics()) return
  isLifted.value = true
}

function unliftField() {
  isLifted.value = false
  fieldMetrics.value = null
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      suppressOpenFromFocus.value = false
      closeCatalog({ restoreFocus: false })
      if (!isBelowLg.value) {
        liftField()
      }
      nextTick(() => {
        if (isBelowLg.value) {
          mobileInputRef.value?.focus()
        }
        else {
          inputRef.value?.focus()
        }
      })
    }
    else {
      // Set before useSearchModal's nextTick focus restore runs (registered earlier
      // in the same close turn). Blur after that restore so focusin cannot reopen.
      suppressOpenFromFocus.value = true
      unliftField()
      nextTick(() => {
        inputRef.value?.blur()
        suppressOpenFromFocus.value = false
      })
    }
  },
)

watch(isBelowLg, () => {
  if (props.open) {
    emit('update:open', false)
  }
})
</script>
